import React, { Fragment, useEffect, useState, useRef } from 'react';
import PaymentServices from '../../Api/Payment/PaymentServices';
import Colors from '../../Assets/Styles/Colors';
import { Autocomplete, Box, Button, capitalize, Chip, Collapse, colors, FormControl, Grid, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { AccountBalance, CancelPresentation, DisabledByDefault, Edit, Euro, FilterAltTwoTone, HighlightOff, KeyboardArrowLeft, KeyboardArrowRight, Paid, Person, Receipt, RemoveRedEye, RequestQuote, TaskAlt, Visibility } from '@mui/icons-material';
import BreadCrumbs from '../../Components/BreadCrumbs/BreadCrumbs';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import AuthServices from '../../Api/Auth/AuthServices';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.module.css"
import "../../../App.css"
import { useTheme } from '@emotion/react';
import CategoriesServices from '../../Api/AdBazar/Categories/CategoriesServices';
import { ErrorToaster, SuccessToaster } from '../../Components/Toaster/Toaster';
import SimpleDialog from '../../Components/Dialogs/SimpleDialog';
import { InputField } from '../../Components/Fields/TextFields';
import { useForm } from 'react-hook-form';
import { PrimaryButton } from '../../Components/Buttons';
import { CircleLoading } from '../../Components/Loaders/CircleLoading';
import { formatDate } from '../../Utils/FormatDate';

const tableHead = [
  "Requested By",
  "Amount",
  "Bank Name",
  "Account No.",
  "Transfered",
  "Actions"
];

function DataFilter(props) {
  const {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    sortData,
  } = props;
  const [filterCollapse, setFilterCollapse] = useState(true);

  const filterData = () => {
    sortData(fromDate && new Date(fromDate), toDate && new Date(toDate));
  };

  const clearData = () => {
    setFromDate(null);
    setToDate(null);
    sortData();
  };

  return (
    <Box sx={{ bgcolor: Colors.white, p: 2, borderRadius: "12px", mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" fontWeight="bold" color="initial">
          Filters
        </Typography>
        <IconButton
          onClick={() => setFilterCollapse(!filterCollapse)}
          color="primary"
        >
          <FilterAltTwoTone sx={{ color: Colors.primary }} />
        </IconButton>
      </Box>

      <Collapse in={filterCollapse}>
        <Grid container spacing={2} alignItems={"flex-end"}>
          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={fromDate}
              onChange={(update) => {
                setFromDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (From)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item md={6}>
            <DatePicker
              showIcon
              toggleCalendarOnIconClick
              selected={toDate}
              onChange={(update) => {
                setToDate(update);
              }}
              isClearable={true}
              placeholderText="Applied Date (To)"
              icon={
                <svg
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  height="1em"
                  width="1em"
                >
                  <path d="M4 .5a.5.5 0 00-1 0V1H2a2 2 0 00-2 2v1h16V3a2 2 0 00-2-2h-1V.5a.5.5 0 00-1 0V1H4V.5zM16 7V5H0v5h5a1 1 0 110 2H0v2a2 2 0 002 2h12a2 2 0 002-2V9h-6a1 1 0 110-2h6z" />
                </svg>
              }
            />
          </Grid>

          <Grid item xs={12} container columnSpacing={2} justifyContent="flex-end">
            <Button
              onClick={clearData}
              variant="outlined"
              sx={{
                mx: 1,
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%), 
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Clear
            </Button>
            <Button
              onClick={filterData}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                minWidth: "100px",
                boxShadow:
                  `0px 3px 1px -2px rgb(0 0 0 / 20%),
                  0px 2px 2px 0px rgb(0 0 0 / 14%),
                  0px 1px 5px 0px rgb(0 0 0 / 12%)`,
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  );
}

function TablePaginationActions(props) {
  const theme = useTheme;

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </Box>
  );
}

function WihtdrawRequest() {
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [count, setCount] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [id, setId] = useState("");
  const [data, setData] = useState(null);
  const [image, setImage] = useState("");
  const [isReasonDialogOpen, setIsReasonDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleReasonDialog = () => setIsReasonDialogOpen((prevOpen) => !prevOpen);
  const handleDetailDialog = () => setIsDetailDialogOpen((prevOpen) => !prevOpen);

  const getWithdrawRequest = async (page, limit, from, to) => {
    setLoading(true);
    try {
      const result = await AuthServices.getWithdrawRequests(
        page ? page : 1,
        limit ? limit : 10,
        from ? formatDate(from) : "",
        to ? formatDate(to) : ""
      );
      if (result.responseCode == 200) {
        setWithdrawRequests(result?.data?.requests);
        setCount(result?.data.totalrequests);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const updateWithdrawRequest = async (obj, type) => {
    setIsLoading(true);
    try {
      const result = await AuthServices.updateWithdrawRequest(id, obj);
      if (result.responseCode == 200) {
        SuccessToaster(result?.message);
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setIsLoading(false);
      if (type == "cancel") {
        handleReasonDialog();
      }
      getWithdrawRequest();
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      try {
        const result = await CategoriesServices.uploadImage(formData);
        const obj = {
          transferImage: result?.data?.imageUrl
        };
        updateWithdrawRequest(obj, type);
      } catch (error) {
        ErrorToaster(error)
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getWithdrawRequest(newPage + 1, rows, fromDate, toDate)
  };

  const handleChangeRowsPerPage = (event) => {
    setRows(Math.floor(event.target.value));
    setPage(0);
    getWithdrawRequest(page, event.target.value, fromDate, toDate);
  };

  const handleIconClick = () => {
    fileInputRef.current.click();
  };

  const cancelWithdrawRequest = async (formData) => {
    const obj = {
      reason: formData.reason,
    };
    await updateWithdrawRequest(obj, "cancel");
  }

  const sort = (from, to) => {
    getWithdrawRequest(page, rows, from, to)
  }

  useEffect(() => {
    getWithdrawRequest();
  }, []);

  return (
    <Fragment>
      <SimpleDialog
        open={isReasonDialogOpen}
        onClose={handleReasonDialog}
        title={"Reason for cancelation?"}
      >
        <Grid
          container
          spacing={2}
          component={"form"}
          onSubmit={handleSubmit(cancelWithdrawRequest)}
        >
          <Grid item md={12}>
            <InputField
              register={register("reason", {
                required: "Please enter a reason",
              })}
              error={errors?.reason?.message}
              helperText={errors?.reason?.message}
              multiline={true}
              rows={5}
            />
          </Grid>
          <Grid item md={12}>
            <PrimaryButton
              loading={isLoading}
              disabled={isLoading}
              fullWidth={true}
              title={"Submit"}
              type={"submit"}
            />
          </Grid>
        </Grid>
      </SimpleDialog>
      <SimpleDialog
        maxWidth={"md"}
        fullWidth={true}
        open={isDetailDialogOpen}
        onClose={handleDetailDialog}
        title={"Withdraw Request Details"}
      >
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Box
              sx={{
                display: "flex",
                alignItem: "flex-start",
                gap: "8px",
                border: "1px solid #e2e2e2",
                borderRadius: "6px",
                p: 2,
                position: "relative"
              }}
            >
              <Box sx={{ px: 1 }}>
                <Person />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  width: "100%"
                }}
              >
                <Typography>
                  User Details:
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    top: "16px",
                    right: "16px"
                  }}
                >
                  <PrimaryButton
                    title={"See Profile"}
                    onClick={() => {
                      if (data?.user?.roles != "Admin") {
                        if (data?.user?.roles == "Individual") {
                          navigate(`/user-details/${data?.user?._id}`, { state: data?.user })
                        } else if (data?.user?.roles == "Service Provier") {
                          navigate(`/service-provider-details/${data?.user?._id}`, { state: data?.user })
                        } else if (data?.user?.roles == "Service Seeker") {
                          navigate(`/service-seeker-details/${data?.user?._id}`, { state: data?.user })
                        }
                      }
                    }}
                  />
                </Box>
                {Object.entries(
                  {
                    name: data?.user?.name,
                    email: data?.user?.email,
                    phone: data?.user?.phone_Number,
                    // residence: `${data?.user?.country}, ${data?.user?.city}, ${data?.user?.state}`,
                  }).map(([key, value], ind) => (
                    <Box
                      key={ind}
                      sx={{
                        display: "flex",
                        gap: "12px"
                      }}
                    >
                      <Typography>
                        {capitalize(key)}:
                      </Typography>
                      <Typography>
                        {value}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          </Grid>
          <Grid item md={6}>
            <Box
              sx={{
                display: "flex",
                alignItem: "flex-start",
                gap: "8px",
                border: "1px solid #e2e2e2",
                borderRadius: "6px",
                p: 2,
              }}
            >
              <Box sx={{ px: 1 }}>
                <AccountBalance />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  height: "100%"
                }}
              >
                <Typography>
                  Bank Details:
                </Typography>
                {Object.entries(
                  {
                    ["Bank Name"]: data?.account?.bankName,
                    ["Account Holder Name"]: data?.account?.accountHolderName,
                    ["Account Number"]: data?.account?.accountNumber,
                  }).map(([key, value], ind) => (
                    <Box
                      key={ind}
                      sx={{
                        display: "flex",
                        gap: "12px"
                      }}
                    >
                      <Typography>
                        {capitalize(key)}:
                      </Typography>
                      <Typography>
                        {value}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          </Grid>
          <Grid item md={6}>
            <Box
              sx={{
                display: "flex",
                alignItem: "center",
                justifyContent: "space-between",
                border: "1px solid #e2e2e2",
                borderRadius: "6px",
                p: 2
              }}
            >
              <RequestQuote />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}
              >
                <Typography>
                  Amount:
                </Typography>
                <Typography>
                  {data?.amount}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item md={6}>
            <Box
              sx={{
                display: "flex",
                alignItem: "center",
                justifyContent: "space-between",
                border: "1px solid #e2e2e2",
                borderRadius: "6px",
                p: 2
              }}
            >
              <Paid />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}
              >
                <Typography>
                  Transfered:
                </Typography>
                {!data?.isWithdrawed ?
                  <Typography>
                    Pending
                  </Typography> :
                  data?.transferImage ?
                    <TaskAlt sx={{ color: colors.blue[800] }} /> :
                    <HighlightOff sx={{ color: colors.blueGrey.A700 }} />
                }
              </Box>
            </Box>
          </Grid>
        </Grid>
      </SimpleDialog>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          background: Colors.primaryGradient,
          p: 2,
          borderRadius: "12px",
        }}
      >
        <Grid item xs={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <IconButton onClick={() => navigate(-1)}>
              <KeyboardArrowLeft sx={{ color: Colors.white }} />
            </IconButton>
            <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
              Withdraw Requests
            </Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <BreadCrumbs currentPage={["Withdraw Requests"]} />
        </Grid>
      </Grid>
      <DataFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        sortData={(from, to) => sort(from, to)}
      />
      <TableContainer
        component={Paper}
        sx={{
          // width: "calc(100vw - 320px)",
          "&::-webkit-scrollbar": {
            bgcolor: Colors.white,
            height: "8px",
            width: "4px",
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: Colors.primary,
            borderRadius: "10px",
            cursor: "pointer",
          },
          "&.MuiPaper-root": {
            borderRadius: "12px",
            mt: 2,
            boxShadow: 0,
          },
        }}
      >
        {loading ? (
          <CircleLoading />
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow sx={{ bgcolor: Colors.lightGray }}>
                {tableHead.map((item, index) => (
                  <TableCell key={index}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        textAlign: item == "Actions" && "center"
                      }}
                    >
                      {item}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawRequests.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {row?.user?.name}
                  </TableCell>
                  <TableCell>
                    {row?.amount}
                  </TableCell>
                  <TableCell>
                    {row?.account?.bankName || "-"}
                  </TableCell>
                  <TableCell>
                    {row?.account?.accountNumber || "-"}
                  </TableCell>
                  <TableCell>
                    {!row?.isWithdrawed ? "Pending" : row?.transferImage ? <TaskAlt sx={{ color: colors.blue[800] }} /> : <HighlightOff sx={{ color: colors.blueGrey.A700 }} />}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2
                      }}
                    >
                      <IconButton onClick={() => { setId(row?._id); handleIconClick(); }}>
                        <Receipt sx={{ color: colors.green[900] }} />
                      </IconButton>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleFileUpload(e, "approve")}
                      />
                      <IconButton onClick={() => { setId(row?._id); handleReasonDialog(); }}>
                        <DisabledByDefault sx={{ color: colors.red[800] }} />
                      </IconButton>
                      <IconButton onClick={() => { setData(row); handleDetailDialog(); }}>
                        <Visibility sx={{ color: Colors.primary }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      <Table sx={{ marginTop: "12px" }}>
        <TablePagination
          sx={{
            borderBottom: "none",
            bgcolor: Colors.white,
            borderRadius: "12px",
          }}
          rowsPerPageOptions={[10, 20, 30]}
          colSpan={12}
          count={+count}
          rowsPerPage={rows}
          page={page}
          SelectProps={{
            inputProps: {
              "aria-label": "rows per page",
            },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Table>
    </Fragment>
  )
}

export default WihtdrawRequest